<template>
  <div
    class="fixed bottom-0 sm:bottom-[unset] sm:top-0 w-full sm:w-16 md:w-24 shrink-0 grow-0 z-50 no-scrollbar border-t sm:border-r border-slate-200 dark:border-slate-700 dark:sm:border-slate-800 bg-slate-100 dark:bg-slate-800 sm:bg-transparent dark:sm:bg-transparent"
    :class="v2LayoutEnabled ? 'sm:h-full' : 'sm:h-screen'"
  >
    <div
      class="sm:h-full flex gap-4 sm:gap-0 flex-row-reverse sm:flex-col justify-between after:w-16 sm:after:w-auto sm:after:flex-1 after:mt-auto after:pointer-events-none w-full overflow-x-auto sm:overflow-x-visible"
    >
      <div class="sm:flex-1 w-1 min-[420px]:w-14 sm:w-auto">
        <div
          class="flex justify-end sm:justify-center mr-6 sm:mr-0 items-center my-4"
          :class="isMobile ? 'opacity-0 pointer-events-none' : ''"
        >
          <DropdownProfile
            :avatar-url="user?.picture"
            :align="isMobile ? 'right' : 'left'"
            class=""
            :hide-settings="
              currentSessionInformation.initializingSession ||
              (!billingStore.hasBypass && !userSubscriptionPriceId)
            "
            @sign-out="doLogout"
          />
        </div>
      </div>
      <div
        class="flex-1 grow flex items-center"
        :class="{
          'opacity-0 pointer-events-none':
            !billingStore.hasBypass && !userSubscriptionPriceId,
        }"
      >
        <nav class="w-full">
          <ul
            class="flex justify-center items-center sm:block space-x-4 min-[360px]:space-x-6 sm:space-y-4 sm:space-x-0 mx-auto sm:mx-0 duration-500"
          >
            <template v-if="isInitializing">
              <li
                v-for="i of 6"
                :key="i"
                class="sm:py-1 h-full sm:h-[unset] flex items-center justify-center"
              >
                <Skeletor :circle="false" class="h-9 w-9 sm:h-8 sm:w-8 rounded-md" />
              </li>
            </template>
            <template v-else>
              <span ref="firstItem"></span>
              <li
                class="sm:py-2 h-full sm:h-[unset] capitalize"
                data-testid="sidebar-home-link"
              >
                <SidebarLink :to="{ name: RouteNames.HOME }" :label="$t('words.home')">
                  <template #icon>
                    <HomeIcon class="h-9 w-8 sm:h-8 sm:w-8 shrink-0" />
                  </template>
                </SidebarLink>
              </li>
              <li class="sm:py-2 h-full sm:h-[unset]" data-testid="tasks-new">
                <SidebarLink
                  :to="{ name: RouteNames.CREATE_TASK }"
                  :label="$t('buttons.create')"
                >
                  <template #icon>
                    <PlusDuotoneIcon class="h-8 w-8 sm:h-8 sm:w-8 shrink-0" />
                  </template>
                </SidebarLink>
              </li>
              <li class="sm:py-2 h-full sm:h-[unset]" data-testid="brand-hub">
                <SidebarLink
                  :to="{ name: RouteNames.BRAIN }"
                  :label="$t('brandHub')"
                  :force-inactive="isFilesRoute"
                >
                  <template #icon>
                    <CpuIcon class="h-9 w-9 sm:h-8 sm:w-8 shrink-0" />
                  </template>
                </SidebarLink>
              </li>
              <li class="sm:py-2 h-full sm:h-[unset]" data-testid="calendar">
                <SidebarLink
                  :to="{ name: RouteNames.CONTENT_CALENDAR }"
                  :label="$t('calendar')"
                >
                  <template #icon>
                    <CalendarIcon class="h-8 w-8 sm:h-8 sm:w-8 shrink-0" />
                  </template>
                </SidebarLink>
              </li>
              <li class="sm:py-2 h-full sm:h-[unset]" data-testid="files">
                <SidebarLink
                  :to="{ name: RouteNames.BRAIN, query: { currentTab: 'files' } }"
                  :label="$t('files')"
                  :is-active="isFilesRoute"
                  :force-inactive="!isFilesRoute"
                >
                  <template #icon>
                    <FileIcon class="h-8 w-8 sm:h-8 sm:w-8 shrink-0" />
                  </template>
                </SidebarLink>
              </li>
              <li class="sm:py-2 h-full sm:h-[unset]" :data-testid="$t('suggestions')">
                <SidebarLink
                  :to="{ name: RouteNames.SMART_TIPS }"
                  :label="$t('smartTips')"
                >
                  <template #icon>
                    <!-- <div class="relative shrink-0">
                      <span
                        v-if="hasSuggestions"
                        class="absolute inline-block -left-2 -top-1 bg-yellow-500 text-white px-2 py-1.5 rounded-full text-xs"
                      >
                        <span class="block h-1 w-1 bg-white rounded-full"></span>
                      </span> -->
                    <BulbIcon class="h-9 w-9 sm:h-8 sm:w-8 shrink-0" />
                    <!-- </div> -->
                  </template>
                </SidebarLink>
              </li>

              <li
                v-if="hasFeatureFlag('messagesUi') && hasEmailIntegration"
                class="sm:py-2"
              >
                <SidebarLink :to="{ name: RouteNames.EMAIL_INBOX }" :label="$t('inbox')">
                  <template #icon>
                    <ChatIcon class="h-9 w-9 sm:h-8 sm:w-8 shrink-0" />
                  </template>
                </SidebarLink>
              </li>
              <li class="sm:py-2" :data-testid="$t('settings.settings')">
                <SidebarLink
                  :to="{ name: RouteNames.SETTINGS }"
                  :label="$t('settings.settings')"
                >
                  <template #icon>
                    <SettingsIcon class="h-10 w-10 sm:h-8 sm:w-8 shrink-0" />
                  </template>
                </SidebarLink>
              </li>
              <span ref="lastItem"></span>
            </template>
          </ul>
        </nav>
      </div>
    </div>
    <div
      v-if="canScrollLeft"
      class="absolute h-full w-7 left-0 bottom-0 z-50 bg-linear-to-r from-surface-900/30 to-transparent pointer-events-none"
    ></div>
    <div
      v-if="canScrollRight"
      class="absolute h-full w-7 right-0 bottom-0 z-50 bg-linear-to-l from-surface-900/30 to-transparent pointer-events-none"
    ></div>
  </div>
</template>

<script lang="ts" setup>
import DropdownProfile from "./DropdownProfile.vue";
import CpuIcon from "@/components/icons/CpuIcon.vue";
import BulbIcon from "@/components/icons/BulbIcon.vue";
import { Skeletor } from "vue-skeletor";
import SettingsIcon from "@/components/icons/SettingsIcon.vue";
import ChatIcon from "@/components/icons/ChatIcon.vue";
import SidebarLink from "@/components/layout/SidebarLink.vue";
import HomeIcon from "@/components/icons/HomeIcon.vue";
import PlusDuotoneIcon from "@/components/icons/PlusDuotoneIcon.vue";
import CalendarIcon from "@/components/icons/CalendarIcon.vue";
import FileIcon from "@/components/icons/FileIcon.vue";

const { user, hasIntegration } = useCurrentUser();
const router = useRouter();
const billingStore = useBillingStore();
const { userSubscriptionPriceId } = toRefs(currentSessionInformation);
// const { isMenuOpen: isV2MenuOpen } = useV2Menu();

async function doLogout() {
  await router.push({ name: RouteNames.LOGOUT });
}

const { isInitializing } = useAppInit();

const hasEmailIntegration = computed(() => hasIntegration("email"));
const firstItem = ref<HTMLLIElement>();
const lastItem = ref<HTMLSpanElement>();
const canScrollLeft = ref(false);
const canScrollRight = ref(false);
const currentRoute = useRoute();
const { v2LayoutEnabled } = useV2Layout();
const isFilesRoute = computed(
  () =>
    currentRoute.name === RouteNames.BRAIN && currentRoute.query.currentTab === "files"
);

const repositionAfterResize = useThrottleFn(() => {
  if (!isMobile.value || !firstItem.value) return;
  const rect = firstItem.value?.getBoundingClientRect();
  if (!rect) return;
  const isScrolledOffScreen = rect.left < 0;
  if (isScrolledOffScreen)
    firstItem.value.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "start",
    });
}, 50);

useIntersectionObserver(firstItem, ([{ isIntersecting: firstIsIntersecting }]) => {
  canScrollLeft.value = !firstIsIntersecting;
});

useIntersectionObserver(lastItem, ([{ isIntersecting: lastIsIntersecting }]) => {
  canScrollRight.value = !lastIsIntersecting;
});

useResizeObserver(document.body, repositionAfterResize);
</script>
